import React, { type FunctionComponent, useEffect, useMemo } from 'react'
import {
  Text,
  NewSelect,
  Tooltip,
  Checkbox,
  PillSelector,
  notify,
  NotificationLevel,
  TextArea,
  colors,
} from '@retailer-platform/shared-components'
import { HelpIcon, TrashIcon } from '@instacart/ids-core'
import { LegacySwitchBase as SwitchBase } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import { Divider } from '@retailer-platform/shared-components/src/tds/index.ts'
import { FormattedMessage } from 'react-intl'
import {
  PropertyContainer,
  PropertySectionLeft,
  PropertySectionRight,
  SectionHeader,
  Property,
  PropertyTitle,
  TextInput,
  GridHeader,
  Grid,
  GridItem,
  TooltipContainer,
  IconGridItem,
  IconButtonContainer,
  EmptyMessage,
  Row,
  PropertyInput,
  PropertyInputButton,
  Input,
  PropertyRow,
} from '../../shared-styled-components/SharedStyledComponents.tsx'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import { PropertyWarning } from '../../md-platform-list/components/MdPlatformListTableRow.tsx'
import { useListMapleSupportedModelsQuery } from '../../../api/index.ts'
import { PropertiesTab } from '../../attribute-management-details/components/properties-tab/PropertiesTab.tsx'
import { type FormProps } from './StepAttributeForm'

const MODEL_PROVIDER_KEY_TO_LABEL = {
  openai: 'OpenAI',
  google: 'Gemini',
  llama: 'Llama',
  anthropic: 'Anthropic',
}

const OutputFormatGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: '10px',
})

const ModelGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto auto 1fr',
  columnGap: '16px',
  rowGap: '8px',
})

const ModelDescriptionItem = styled.div(({ color }: { color?: string }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
  color: color || 'inherit',
}))

const ModeGrid = styled.div({
  marginTop: '16px',
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  columnGap: '16px',
  rowGap: '8px',
})

const ModeLabel = styled.div({
  justifySelf: 'end',
})

const ModelDescription = ({ batch, sync, imageInput, jsonOutput, costs }: any) => (
  <ModelGrid>
    <ModelDescriptionItem color={imageInput ? 'green' : 'red'}>
      <Text size="small" weight="semibold">
        images allowed:
      </Text>
      <Text size="small">{imageInput ? 'yes' : 'no'}</Text>
    </ModelDescriptionItem>
    <div />
    <div />
    <ModelDescriptionItem color={jsonOutput ? 'green' : 'red'}>
      <Text size="small" weight="semibold">
        Allows JSON output:
      </Text>
      <Text size="small">{jsonOutput ? 'yes' : 'no'}</Text>
    </ModelDescriptionItem>
    <div />
    <div />
    <ModelDescriptionItem color={batch ? 'green' : 'red'}>
      <Text size="small" weight="semibold">
        batches:
      </Text>
      <Text size="small">{batch ? 'yes' : 'no'}</Text>
    </ModelDescriptionItem>
    <ModelDescriptionItem>
      <Text size="small" weight="semibold">
        input cost:
      </Text>
      <Text size="small">
        {batch
          ? `$${costs?.batch_input_price || costs?.input_price || 'error!'} /1M tokens`
          : 'N/A'}
      </Text>
    </ModelDescriptionItem>
    <ModelDescriptionItem>
      <Text size="small" weight="semibold">
        output cost:
      </Text>
      <Text size="small">
        {batch
          ? `$${costs?.batch_output_price || costs?.output_price || 'error!'} /1M tokens`
          : 'N/A'}
      </Text>
    </ModelDescriptionItem>
    <ModelDescriptionItem color={sync ? 'green' : 'red'}>
      <Text size="small" weight="semibold">
        sync:
      </Text>
      <Text size="small">{sync ? 'yes' : 'no'}</Text>
    </ModelDescriptionItem>
    <ModelDescriptionItem>
      <Text size="small" weight="semibold">
        input cost:
      </Text>
      <Text size="small">{sync ? `$${costs?.input_price || 'error!'} /1M tokens` : 'N/A'}</Text>
    </ModelDescriptionItem>
    <ModelDescriptionItem>
      <Text size="small" weight="semibold">
        output cost:
      </Text>
      <Text size="small">{sync ? `$${costs?.output_price || 'error!'} /1M tokens` : 'N/A'}</Text>
    </ModelDescriptionItem>
  </ModelGrid>
)

export const LLMPromptTemplateForm: FunctionComponent<FormProps> = ({
  attributes,
  mode,
  onChange,
}) => {
  const i18n = useDomainMessages({
    model: 'catalogAdminDomain.attributeManagement.modal.model',
    propertyName: 'catalogAdminDomain.attributeManagement.modal.table.headers.propertyName',
    required: 'catalogAdminDomain.attributeManagement.modal.table.headers.required',
    images: 'catalogAdminDomain.attributeManagement.modal.table.headers.images',
    modelPlaceholder: 'catalogAdminDomain.attributeManagement.modal.modelPlaceholder',
    EmptyPropertyTable: 'catalogAdminDomain.attributeManagement.table.property.empty.message',
    PropertyTablePlaceholder: 'catalogAdminDomain.attributeManagement.property.input.placeholder',
    addProperty: 'catalogAdminDomain.attributeManagement.property.input.button.add',
    deleteProperty: 'catalogAdminDomain.attributeManagement.property.delete',
    requiredHelp: 'catalogAdminDomain.attributeManagement.label.required.help',
    imagesHelp: 'catalogAdminDomain.attributeManagement.label.images.help',
    propertyAlreadyExists: 'catalogAdminDomain.attributeManagement.create.property.warning.message',
    prompt: 'catalogAdminDomain.attributeManagement.modal.prompt',
    labelProperties: 'catalogAdminDomain.attributeManagement.create.label.properties',
    labelPropertiesSubtile: 'catalogAdminDomain.attributeManagement.create.label.property.subtitle',
    outputPrefix: 'catalogAdminDomain.attributeManagement.form.outputPrefix',
    llmPromptTemplate: 'catalogAdminDomain.attributeManagement.form.llmPromptTemplate',
    appendTaskDataDescription:
      'catalogAdminDomain.attributeManagement.form.inputAppendTaskDataDescription',
    appendTaskDataHelp: 'catalogAdminDomain.attributeManagement.form.inputAppendTaskDataHelp',
    outputStructuredDataDescription:
      'catalogAdminDomain.attributeManagement.form.outputStructuredDataDescription',
    outputStructuredDataHelp:
      'catalogAdminDomain.attributeManagement.form.outputStructuredDataHelp',
    outputPrefixHelp: 'catalogAdminDomain.attributeManagement.form.outputPrefixHelp',
    outputPrefixDescription: 'catalogAdminDomain.attributeManagement.form.outputPrefixDescription',
    outputPrefixOutputFields:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFields',
    default: 'catalogAdminDomain.attributeManagement.form.default',
    outputPrefixOutputFieldsValue:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsValue',
    outputPrefixOutputFieldsExplanation:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsExplanation',
    outputPrefixOutputFieldsConfidence:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsConfidence',
    outputPrefixOutputFieldsPrompt:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsPrompt',
    outputPrefixOutputFieldsModel:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsModel',
    outputPrefixOutputFieldsCost:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsCost',
    outputPrefixOutputFieldsTotalCost:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsTotalCost',
    flattenOutputHelp: 'catalogAdminDomain.attributeManagement.form.flattenOutputHelp',
    flattenOutputHelpBold: 'catalogAdminDomain.attributeManagement.form.flattenOutputHelpBold',
    outputPrefixOutputFieldsMapleInputValues:
      'catalogAdminDomain.attributeManagement.form.outputPrefixOutputFieldsMapleInputValues',
  })

  const [currentOutputFormat, setCurrentOutputFormat] = React.useState<any | null | undefined>(
    undefined
  )
  const [propertyName, setPropertyName] = React.useState<string>('')
  const validPropertyName = !attributes?.properties.find(
    (p: any) => p.propertyName === propertyName
  )

  const { data } = useListMapleSupportedModelsQuery()
  const modelProviders = data?.instacart_flawless_inventory_v1_MapleService_GetMapleSupportedModels
    ?.supported_models
    ? JSON.parse(
        data?.instacart_flawless_inventory_v1_MapleService_GetMapleSupportedModels?.supported_models
      )
    : undefined

  const modelOptions = React.useMemo(() => {
    if (!modelProviders) {
      return []
    }

    const modelOptions = Object.keys(modelProviders)
      .sort()
      .map(key => ({
        label: MODEL_PROVIDER_KEY_TO_LABEL[key as keyof typeof MODEL_PROVIDER_KEY_TO_LABEL] || key,
        options: Object.keys(modelProviders[key])
          .sort()
          .map((modelName: string) => ({
            label: modelName,
            value: modelName,
            description: (
              <ModelDescription
                batch={modelProviders[key][modelName].batch}
                sync={modelProviders[key][modelName].sync}
                imageInput={modelProviders[key][modelName].image_input}
                jsonOutput={modelProviders[key][modelName].supports_json_output}
                costs={modelProviders[key][modelName].costs}
              />
            ),
          })),
      }))

    // The UX for 2 groups is not great; there's no divider between groups. so we add another group to make it 3.
    if (modelOptions.length === 2) {
      modelOptions.push({
        label: 'llama',
        // @ts-ignore - this is already a hack, so ignore the type error.
        options: [{ label: 'Coming soon', value: 'Coming soon' }],
      })
    }
    return modelOptions
  }, [modelProviders])

  const selectedModel = React.useMemo(() => {
    if (!attributes.model || !modelProviders) {
      return null
    }
    for (const provider in modelProviders) {
      for (const model in modelProviders[provider]) {
        if (model === attributes.model) {
          return modelProviders[provider][model]
        }
      }
    }
  }, [modelProviders, attributes.model])

  useEffect(() => {
    // Clear previous input if we've selected a model that doesn't support structured output
    const hasOutputSupport = selectedModel?.supports_structured_outputs ?? false
    if (!hasOutputSupport && attributes?.structuredOutput) {
      onChange({
        ...attributes,
        structuredOutput: null,
      })
    }
  }, [attributes, onChange, selectedModel?.supports_structured_outputs])

  useEffect(() => {
    // Initialize our current state
    if (currentOutputFormat !== undefined) return
    const structuredOutput = attributes?.structuredOutput
    if (structuredOutput) {
      try {
        setCurrentOutputFormat(JSON.stringify(structuredOutput, null, 3))
      } catch {
        setCurrentOutputFormat(null)
      }
    } else {
      setCurrentOutputFormat(null)
    }
  }, [attributes, currentOutputFormat])

  const [structuredOutputError, structuredOutput] = useMemo(() => {
    // Attempt to parse the user's input, if valid we save otherwise we emit an error
    if (!currentOutputFormat) return [null, null]

    try {
      const parsed = JSON.parse(currentOutputFormat)
      return [null, parsed]
    } catch (error) {
      return [String(error), null]
    }
  }, [currentOutputFormat])

  useEffect(() => {
    // Anytime we parse a valid output, we trigger a save.
    if (attributes?.structuredOutput != structuredOutput) {
      onChange({
        ...attributes,
        structuredOutput: structuredOutput,
      })
    }
  }, [onChange, attributes, structuredOutput])

  const productionOperationModel = React.useMemo(() => {
    if (!selectedModel) {
      return attributes.productionOperationModel || 'sync'
    }
    if (attributes.productionOperationModel) {
      return attributes.productionOperationModel
    }
    return selectedModel.batch ? 'batch' : 'sync'
  }, [selectedModel, attributes.productionOperationModel])
  const testOperationModel = React.useMemo(() => {
    if (!selectedModel) {
      return attributes.testOperationModel || 'sync'
    }
    if (attributes.testOperationModel) {
      return attributes.testOperationModel
    }
    return selectedModel.sync ? 'sync' : 'batch'
  }, [selectedModel, attributes.testOperationModel])

  const PropertyNameWarning = () => (
    <PropertyWarning error={true}>• {i18n.propertyAlreadyExists}</PropertyWarning>
  )

  return (
    <>
      <SectionHeader> {i18n.llmPromptTemplate} </SectionHeader>
      <PropertyContainer>
        <PropertySectionLeft>
          <Property>
            <PropertyTitle>{i18n.model}</PropertyTitle>
            <NewSelect
              isDisabled={mode !== 'edit'}
              placeholder={i18n.modelPlaceholder}
              isLoading={!data}
              onChange={value => {
                for (const provider in modelProviders) {
                  for (const model in modelProviders[provider]) {
                    if (model === value) {
                      // Test that this model is compatible with the current settings
                      const allowsImages = modelProviders[provider][model].image_input
                      const allowsJsonOutput = modelProviders[provider][model].supports_json_output
                      const allowsSync = modelProviders[provider][model].sync
                      const allowsBatch = modelProviders[provider][model].batch

                      if (
                        (attributes.testOperationModel === 'sync' && !allowsSync) ||
                        (attributes.productionOperationModel === 'sync' && !allowsSync)
                      ) {
                        notify({
                          level: NotificationLevel.Error,
                          body: (
                            <div>
                              Sync mode is not supported for this model. Please first disable sync
                              mode in Operation Model section below before changing to this model.
                            </div>
                          ),
                        })
                        return
                      }

                      if (
                        (attributes.testOperationModel === 'batch' && !allowsBatch) ||
                        (attributes.productionOperationModel === 'batch' && !allowsBatch)
                      ) {
                        notify({
                          level: NotificationLevel.Error,
                          body: (
                            <div>
                              Batch mode is not supported for this model. Please first disable batch
                              mode in Operation Model section below before changing to this model.
                            </div>
                          ),
                        })
                        return
                      }
                      if (attributes.flattenOutput && !allowsJsonOutput) {
                        notify({
                          level: NotificationLevel.Error,
                          body: (
                            <div>
                              JSON output is not supported for this model. Please unselect the
                              Flatten Output switch before switching to this model.
                            </div>
                          ),
                        })
                        return
                      }

                      if (
                        (attributes.properties || []).some((property: any) => property.images) &&
                        !allowsImages
                      ) {
                        notify({
                          level: NotificationLevel.Error,
                          body: (
                            <div>
                              Images are not supported for this model. Please remove the images from
                              the properties below before switching to this model.
                            </div>
                          ),
                        })
                        return
                      }
                    }
                  }
                }
                // If the model is compatible, update the state
                if (value)
                  onChange({
                    ...attributes,
                    model: value || '',
                  })
              }}
              options={modelOptions}
              value={attributes.model || ''}
            />
          </Property>
          <Property>
            <PropertyTitle>AI Gateway Source ID</PropertyTitle>
            <div>
              <Text size="small" display="inline">
                This is your AI Gateway Source ID needed to track costs. to get your source id,
                please follow the instructions&nbsp;
              </Text>
              <Text display="inline" size="small" variant="link">
                <a
                  href="https://instacart.atlassian.net/wiki/spaces/Catalog/pages/5147951585/Providing+an+AI+Gateway+Source+for+MAPLE#Creating-a-Source"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
              </Text>
            </div>
            <Input
              type="text"
              error={mode === 'edit' && !attributes.costAttributionSourceId?.length}
              disabled={mode !== 'edit'}
              value={attributes.costAttributionSourceId || ''}
              onChange={e => {
                onChange({ ...attributes, costAttributionSourceId: e.target.value })
              }}
            />
          </Property>
          <Property>
            <PropertyTitle>Operation Model</PropertyTitle>
            <div>
              <Text size="small" display="inline">
                Select whether you want to run your prompts in sync or batched mode. Sync mode has a
                more reliable time to completion, but on average takes longer and is more expensive.
                Batches are on average faster and are 50% cheaper, but in the worst case can take
                much longer to complete, occasionally over 24 hours. Support for sync in production
                in progress.
              </Text>
              <ModeGrid>
                <ModeLabel>
                  <Text size="small" weight="semibold">
                    Tests:{' '}
                  </Text>
                </ModeLabel>
                <PillSelector
                  disabled={
                    mode !== 'edit' ||
                    (selectedModel && (!selectedModel.batch || !selectedModel.sync))
                  }
                  items={[
                    { label: 'Sync', value: 'sync' },
                    { label: 'Batch', value: 'batch' },
                  ]}
                  value={testOperationModel}
                  onChange={e => {
                    onChange({
                      ...attributes,
                      testOperationModel: e.target.value,
                    })
                  }}
                />
                <ModeLabel>
                  <Text size="small" weight="semibold">
                    Production:{' '}
                  </Text>
                </ModeLabel>
                <PillSelector
                  disabled={
                    mode !== 'edit' ||
                    (selectedModel && (!selectedModel.batch || !selectedModel.sync))
                  }
                  items={[
                    { label: 'Sync', value: 'sync' },
                    { label: 'Batch', value: 'batch' },
                  ]}
                  value={productionOperationModel}
                  onChange={e => {
                    onChange({
                      ...attributes,
                      productionOperationModel: e.target.value,
                    })
                  }}
                />
              </ModeGrid>
            </div>
          </Property>
          <Property>
            <PropertyTitle>{i18n.labelProperties} </PropertyTitle>
            <Text size="small">{i18n.labelPropertiesSubtile}</Text>
            <Grid>
              <GridHeader>{i18n.propertyName}</GridHeader>
              <GridHeader>
                <TooltipContainer>
                  {i18n.required}
                  <Tooltip placement="bottom" target={<HelpIcon />}>
                    {i18n.requiredHelp}
                  </Tooltip>
                </TooltipContainer>
              </GridHeader>
              <GridHeader>
                <TooltipContainer>
                  {i18n.images}
                  <Tooltip placement="bottom" target={<HelpIcon />}>
                    {i18n.imagesHelp}
                  </Tooltip>
                </TooltipContainer>
              </GridHeader>
              <GridHeader> {i18n.deleteProperty}</GridHeader>
              {attributes.properties?.map((property: any) => (
                <>
                  <GridItem> {property.propertyName} </GridItem>
                  <GridItem>
                    <Checkbox
                      disabled={mode !== 'edit'}
                      checked={property.required}
                      onChange={() => {
                        onChange({
                          ...attributes,
                          properties: attributes.properties.map((p: any) =>
                            p.propertyName === property.propertyName
                              ? { ...p, required: !p.required }
                              : p
                          ),
                        })
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <Checkbox
                      disabled={
                        mode !== 'edit' || (!selectedModel?.image_input && !property.images)
                      }
                      indeterminate={!selectedModel?.image_input && property.images}
                      checked={property.images}
                      onChange={() => {
                        onChange({
                          ...attributes,
                          properties: attributes.properties.map((p: any) =>
                            p.propertyName === property.propertyName
                              ? { ...p, images: !p.images }
                              : p
                          ),
                        })
                      }}
                    />
                  </GridItem>
                  <IconGridItem>
                    <IconButtonContainer
                      disabled={mode !== 'edit'}
                      icon={TrashIcon}
                      onPress={() => {
                        onChange({
                          ...attributes,
                          properties: attributes.properties.filter(
                            (p: any) => p.propertyName !== property.propertyName
                          ),
                        })
                      }}
                      accessibleLabel={'Delete'}
                    ></IconButtonContainer>
                  </IconGridItem>
                </>
              ))}
            </Grid>
            {!validPropertyName && <PropertyNameWarning />}
            {attributes?.properties?.length === 0 && (
              <EmptyMessage> {i18n.EmptyPropertyTable} </EmptyMessage>
            )}
            {mode === 'edit' && (
              <Row>
                <PropertyInput
                  placeholder={i18n.PropertyTablePlaceholder}
                  value={propertyName}
                  onChange={event => setPropertyName(event.target.value)}
                  error={!validPropertyName || attributes.properties.length === 0}
                ></PropertyInput>
                <PropertyInputButton
                  variant={'secondary'}
                  onClick={() => {
                    propertyName !== '' &&
                      onChange({
                        ...attributes,
                        properties: [
                          ...attributes.properties,
                          {
                            propertyName,
                            required: false,
                            images: false,
                          },
                        ],
                      })
                    setPropertyName('')
                  }}
                  disabled={propertyName === '' || !validPropertyName}
                >
                  {i18n.addProperty}
                </PropertyInputButton>
              </Row>
            )}
          </Property>

          <Divider />
          <SectionHeader>Input Controls</SectionHeader>
          <Property>
            <PropertyTitle>
              {i18n.appendTaskDataDescription}
              <SwitchBase
                checked={attributes?.appendTaskData ?? true}
                disabled={mode === 'view'}
                description={i18n.appendTaskDataDescription}
                onChange={event =>
                  onChange({
                    ...attributes,
                    appendTaskData: event.target.checked,
                  })
                }
              />
            </PropertyTitle>
            <Text size="small">{i18n.appendTaskDataHelp}</Text>
          </Property>
          <Divider />
          <SectionHeader>Output Controls</SectionHeader>
          <Property>
            <PropertyTitle>
              {i18n.outputPrefix}
              <SwitchBase
                checked={attributes.usePrefix}
                disabled={mode === 'view'}
                description={i18n.outputPrefixDescription}
                onChange={event =>
                  onChange({
                    ...attributes,
                    usePrefix: event.target.checked,
                  })
                }
              />
            </PropertyTitle>
            <Text size="small">{i18n.outputPrefixHelp}</Text>
            <PropertyRow>
              <Input
                style={{ flex: 1 }}
                type="text"
                disabled={!attributes.usePrefix || mode === 'view'}
                error={attributes.usePrefix && !attributes?.outputPrefix?.length}
                value={attributes.usePrefix ? attributes.outputPrefix : i18n.default}
                onChange={e => {
                  attributes.usePrefix && onChange({ ...attributes, outputPrefix: e.target.value })
                }}
              />
            </PropertyRow>
          </Property>
          {!!selectedModel?.supports_structured_outputs && (
            <Property>
              <PropertyTitle>
                {i18n.outputStructuredDataDescription}
                <SwitchBase
                  checked={currentOutputFormat != null}
                  disabled={mode === 'view'}
                  description={i18n.outputStructuredDataDescription}
                  onChange={event => {
                    setCurrentOutputFormat(event.target.checked ? '' : null)
                  }}
                />
              </PropertyTitle>
              <Text size="small">
                <FormattedMessage
                  id="catalogAdminDomain.attributeManagement.form.outputStructuredDataHelp"
                  values={{
                    b: (...chunks) => (
                      <Text size="small" weight={'bold'}>
                        {chunks}
                      </Text>
                    ),
                  }}
                />
              </Text>
              {currentOutputFormat != null ? (
                <>
                  {!!structuredOutputError && (
                    <Text size="small" css={{ color: colors.DETRIMENTAL.REGULAR }}>
                      {structuredOutputError}
                    </Text>
                  )}
                  <PropertyRow>
                    <TextArea
                      style={{ flex: 1, resize: 'vertical', height: '400px' }}
                      disabled={mode === 'view'}
                      value={currentOutputFormat}
                      onChange={e => {
                        setCurrentOutputFormat(e.target.value)
                      }}
                    />
                  </PropertyRow>
                </>
              ) : null}
            </Property>
          )}
          <Property>
            <PropertyTitle>
              Flatten Output
              <SwitchBase
                checked={attributes.flattenOutput}
                disabled={mode === 'view'}
                description={'enable flattening of LLM output'}
                onChange={event =>
                  onChange({
                    ...attributes,
                    flattenOutput: event.target.checked,
                  })
                }
              />
            </PropertyTitle>
            <Text size="small">{i18n.flattenOutputHelp}</Text>
            <Text weight="semibold" size="small">
              {i18n.flattenOutputHelpBold}
            </Text>
          </Property>
          <Divider />
          <SectionHeader> Output Example </SectionHeader>
          <Property>
            {attributes.flattenOutput && (
              <Text size="small">
                For this example, assume the response from the LLM is this json object: <br />
                {'{'}
                <br />
                &nbsp;&nbsp;&quot;product_name&quot;: &quot;Tortilla Chips Nacho Cheese Flavored
                14.5 oz Party Size&quot;,
                <br />
                &nbsp;&nbsp;&quot;assessment&quot;: &quot;Mismatch&quot;
                <br />
                {'}'}
                <br />
              </Text>
            )}
            <Text size="small">{i18n.outputPrefixOutputFields}</Text>

            <OutputFormatGrid>
              <Text size="small" weight="bold">
                Output Field
              </Text>
              <Text size="small" weight="bold">
                Field Explanation
              </Text>
              {attributes.flattenOutput ? (
                <>
                  <Text size="small" weight="semibold">
                    {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'product_name'}:
                  </Text>
                  <Text size="small">
                    flattened from the LLM output. <br />
                    Value would be &quot;Tortilla Chips Nacho Cheese Flavored 14.5 oz Party
                    Size&quot;
                  </Text>
                  <Text size="small" weight="semibold">
                    {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'assessment'}:
                  </Text>
                  <Text size="small">
                    flattened from the LLM output. <br />
                    Value would be &quot;Mismatch&quot;
                  </Text>
                </>
              ) : (
                <>
                  <Text size="small" weight="semibold">
                    {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'ai_value'}:
                  </Text>
                  <Text size="small">{i18n.outputPrefixOutputFieldsValue}</Text>
                </>
              )}
              <Text size="small" weight="semibold">
                {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'ai_cost'}:
              </Text>
              <Text size="small">{i18n.outputPrefixOutputFieldsCost}</Text>
              <Text size="small" weight="semibold">
                {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'maple_input_values'}
                :
              </Text>
              <Text size="small">{i18n.outputPrefixOutputFieldsMapleInputValues}</Text>
              <Text size="small" weight="semibold">
                {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'prompt'}:
              </Text>
              <Text size="small">{i18n.outputPrefixOutputFieldsPrompt}</Text>
              <Text size="small" weight="semibold">
                {(attributes.usePrefix ? attributes.outputPrefix + '_' : '') + 'model'}:
              </Text>
              <Text size="small">{i18n.outputPrefixOutputFieldsModel}</Text>
              <Text size="small" weight="semibold">
                cost:
              </Text>
              <Text size="small">{i18n.outputPrefixOutputFieldsTotalCost}</Text>
            </OutputFormatGrid>
          </Property>
        </PropertySectionLeft>
        <PropertySectionRight>
          <Property style={{ height: '100%' }}>
            <PropertyTitle>{i18n.prompt}</PropertyTitle>
            <TextInput
              disabled={mode !== 'edit'}
              onChange={e => {
                onChange({
                  ...attributes,
                  prompt: e.target.value,
                })
              }}
              value={attributes.prompt}
              error={attributes.prompt.length === 0 && mode === 'edit'}
            />
          </Property>
        </PropertySectionRight>
      </PropertyContainer>
    </>
  )
}
